import {Component, DoCheck, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FilterType} from './filters-type.enum';
import {IFilterOption, IFiltersConfig} from './filters-config.interface';
import {FiltersModel} from './filters.model';
import * as _ from 'lodash';
import {BehaviorSubject, from, Observable, pipe, Subject, Subscription} from 'rxjs/index';
import {FiltersConfig} from './filters.config';
import {debounceTime} from 'rxjs/internal/operators';

@Component({
  selector: 'hxa-filters',
  template: `<div class="hx-flex hx-flex-align-center">

  <i  class="hx-icon icon-filter-outline mx-1"></i>

  <!-- collapsed filters -->
  <hxa-filters-collapsed (onFilter)="onCollapsedFilter($event)" (onSearchFilter)="onCollapsedSearch($event)" [data]="data" *ngIf="collapsed"></hxa-filters-collapsed>

  <!-- expanded filters -->
  <ng-container *ngIf="!collapsed">
    <ng-container *ngFor="let filter of data;trackBy: trackByFn;let lastItem = last;">

    <!-- single select filter -->
    <div [id]="filter?.id" class="hx-dropdown mx-1 expandedFilter" hxaDropdown [minWidthRelativeTo]="filter?.id + '-trigger'"  *ngIf="filter?.type === FilterType.SingleSelect">
      <a [id]="filter?.id + '-trigger'" class="hx-button hx-button-dropdown expandedFilter__button" hxaDropdownToggle>
        <span class="is-text-weight-light">{{filter?.label}}:&nbsp;</span>
        <span class="hx-icon-control" *ngIf="filter?.selected?.icon"><i class="{{filter?.selected?.icon}} mr-1"></i></span>
        <span class="is-text-weight-bolder is-text-ellipsed" title="{{filter?.selected?.label}}">{{filter?.selected?.label}}</span>
        <span class="hx-icon-control"><i class="hx-icon icon-caret-down"></i></span>
      </a>
      <div [id]="filter?.id + '-options'" class="hx-dropdown-menu  has-limited-width has-limited-height expandedFilter__menu" [class.hx-dropdown-icon]="filter.isIconised()" *hxaDropdownMenu>
        <a hxaDropdownItem *ngFor="let option of filter?.options;trackBy: trackByFn" (click)="onFilterOptionSelected(filter,option)" class="hx-dropdown-item is-text-ellipsed" title="{{option?.label}}"> <span class="hx-icon-control" *ngIf="option?.icon"><i class="{{option?.icon}}"></i></span> {{option?.label}}</a>
      </div>
    </div>

    <!-- search filter -->
    <div [id]="filter.id" *ngIf="filter.type === FilterType.Search"  class="hx-input-group ml-4" [class.mx-1]="!lastItem">
      <i class="hx-icon icon-search mt-1"></i>
      <div class="hx-input-control my-0">
        <input class="hx-input"
               type="search"
               placeholder="{{filter.label}}"
               (input)="onSearchFilterChange(filter)"
               [(ngModel)]="filter.value">
        <label class="hx-label"></label>
        <div class="hx-help"></div>
      </div>
      <div class="hx-input-actions" *ngIf="filter.value">
        <!--<div id="searchLoadingIndicator" class="hx-loader is-small" *ngIf="patientSearchService.showPatientSearchLoader"><div></div><div></div><div></div><div></div></div>-->
        <div class="hx-button-group">
          <button class="hx-button is-flat" (click)="clearSearch(filter)"><span class="hx-icon-control"><i class="icon icon-close-empty"></i></span></button>
        </div>
      </div>
    </div>
  </ng-container>
  </ng-container>
</div>
`,
  styles: [`:host .expandedFilter__button{max-width:15rem}:host .expandedFilter__menu{width:100%;max-height:20rem;max-width:15rem;overflow-x:hidden;overflow-y:auto}`]
})
export class FiltersComponent implements OnInit, DoCheck, OnDestroy {

  @ViewChild('carousel') private carousel: ElementRef;

  FilterType = FilterType;
  data: FiltersModel[] = [];
  onFilterOptionChanged$ = new Subject<FiltersModel>();
  searchFilter$: Subject<FiltersModel> = new Subject<FiltersModel>();
  subscriptions: Subscription = new Subscription();

  private _filters: IFiltersConfig[] = [];
  private _oldFilters: IFiltersConfig[] = [];
  private _collapsed = false;

  @Input()
  get collapsed(): boolean {
    return this._collapsed;
  }

  set collapsed(value: boolean) {
    this._collapsed = value;
  }

  @Input()
  get filters(): IFiltersConfig[] {
    return this._filters;
  }

  set filters(value: IFiltersConfig[]) {
    this._filters = value;
    this.setData();
  }

  constructor(
    private conf: FiltersConfig
  ) {
    Object.assign(this, conf);
  }


  ngOnInit() {
    this.subscriptions.add(
      this.searchFilter$
        .pipe(debounceTime(this.conf.debounce))
        .subscribe((x) =>  this.onFilterOptionChanged$.next(x))
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  ngDoCheck() {
    if (!_.isEqual(this._filters, this._oldFilters)) {
      this._oldFilters = _.cloneDeep(this._filters);
      this.setData();
    }
  }

  resetFilters(silent: boolean = false) {
    for (const filter of this.data) {
      if (filter.type === FilterType.SingleSelect) {
        filter.setDefaultOption();
        if (!silent) {
          this.onFilterOptionChanged$.next(filter);
        }
      } else if (filter.type === FilterType.Search) {
        this.clearSearch(filter, silent);
      }
    }
  }

  clearSearch(filter: FiltersModel, silent: boolean = false) {
      filter.value = '';
      if (!silent) {
        this.onFilterOptionChanged$.next(filter);
      }
  }


  /**
   * Called when filter option is selected
   */
  onFilterOptionSelected(filter: FiltersModel, option: IFilterOption) {
    filter.setSelectedOption(option);
    this.onFilterOptionChanged$.next(filter);
  }

  /**
   * Called when character is typed in the search filter type
   */
  onSearchFilterChange(filter: FiltersModel) {
    if (filter.value.length === 0 || filter.value.length >= filter.charLimit) {
      this.searchFilter$.next(filter);
    }
  }


  onCollapsedFilter($event) {
   this.onFilterOptionSelected($event.filter,  $event.option);
  }

  onCollapsedSearch($event) {
    this.onSearchFilterChange($event.filter);
  }


  /**
   * Used for track by and boost performance
   */
  trackByFn(index, action) {
    return index;
  }

  /**
   * Convert filter config objects to Filter Models
   */
  setData() {
    this.data = [];
    if (this._filters) {
      this._filters.forEach((filter: IFiltersConfig, index) => {
        this.data.push(new FiltersModel(_.cloneDeep(filter)));
      });
    }
  }
}
